import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  QrCode, 
  Home,
  Store,
  Settings,
  FolderTree,
  Package,
  BarChart3,
  CreditCard,
  Menu,
  X,
  LogOut,
  User,
  Sun,
  Moon,
  ChevronLeft,
  ImageIcon,
  FileDown,
  LayoutDashboard,
  Users,
  Scale
} from 'lucide-react';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { shopId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const mainNavItems = [
    { path: '/app', icon: Home, label: 'Accueil', exact: true },
    { path: '/app/shops', icon: Store, label: 'Mes boutiques' },
    { path: '/app/billing', icon: CreditCard, label: 'Abonnement' },
  ];

  const shopNavItems = shopId ? [
    { path: `/app/shops/${shopId}/settings`, icon: Settings, label: 'Param\u00e8tres' },
    { path: `/app/shops/${shopId}/categories`, icon: FolderTree, label: 'Cat\u00e9gories' },
    { path: `/app/shops/${shopId}/products`, icon: Package, label: 'Produits' },
    { path: `/app/shops/${shopId}/banners`, icon: ImageIcon, label: 'Banni\u00e8res' },
    { path: `/app/shops/${shopId}/qr`, icon: QrCode, label: 'QR Code' },
    { path: `/app/shops/${shopId}/analytics`, icon: BarChart3, label: 'Statistiques' },
    { path: `/app/shops/${shopId}/export`, icon: FileDown, label: 'Export PDF' },
    { path: `/app/shops/${shopId}/page-builder`, icon: LayoutDashboard, label: "Page d'accueil" },
    ...(user?.plan === 'PREMIUM' ? [{ path: `/app/shops/${shopId}/staff`, icon: Users, label: '\u00c9quipe' }] : []),
    { path: `/app/shops/${shopId}/legal`, icon: Scale, label: 'Mentions legales' },
  ] : [];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const NavLink = ({ item }) => (
    <Link
      to={item.path}
      onClick={() => setSidebarOpen(false)}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
        isActive(item.path, item.exact)
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <item.icon className="w-5 h-5" />
      <span className="font-medium">{item.label}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b h-16 flex items-center px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu className="w-6 h-6" />
        </Button>
        <div className="flex-1 flex justify-center">
          <Link to="/app" className="flex items-center space-x-2">
            <QrCode className="w-6 h-6 text-primary" />
            <span className="font-bold font-['Manrope']">IziCatalog</span>
          </Link>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
        </Button>
      </header>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-card border-r transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b">
            <Link to="/app" className="flex items-center space-x-2">
              <QrCode className="w-8 h-8 text-primary" />
              <span className="text-xl font-bold font-['Manrope']">IziCatalog</span>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {mainNavItems.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
            </nav>

            {shopId && (
              <>
                <div className="my-4 px-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-muted-foreground"
                    onClick={() => navigate('/app/shops')}
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Retour aux boutiques
                  </Button>
                </div>
                <div className="px-3 mb-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Gestion boutique
                  </p>
                </div>
                <nav className="space-y-1">
                  {shopNavItems.map((item) => (
                    <NavLink key={item.path} item={item} />
                  ))}
                </nav>
              </>
            )}
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.plan}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === 'light' ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
                  {theme === 'light' ? 'Mode sombre' : 'Mode clair'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
